import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, Building2, Smartphone, Sparkles } from 'lucide-react';
import type { Role } from '../../shared/types/domain';
import { Button } from '../../shared/ui/Button';
import { Card } from '../../shared/ui/Card';
import { useAuth } from './AuthProvider';

const loginSchema = z.object({
  phone: z.string().regex(/^1\d{10}$/, '请输入正确的11位手机号'),
  password: z.string().min(8, '请输入8位登录密码'),
});
type LoginForm = z.infer<typeof loginSchema>;

const demoRoles: { role: Role; label: string; scope: string }[] = [
  { role: 'member', label: '会员端', scope: '仅本人课程与消费' },
  { role: 'employee', label: '员工端', scope: '负责会员与前台职能' },
  { role: 'manager', label: '店长端', scope: '仅本门店经营管理' },
  { role: 'super', label: '超管端', scope: '全公司治理与审计' },
];

export function LoginPage() {
  const auth = useAuth();
  const loginForm = useForm<LoginForm>({ resolver: zodResolver(loginSchema), defaultValues: { phone: '', password: '' } });

  return (
    <main className="login-shell">
      <section className="login-intro">
        <div className="brand-lockup"><span><Sparkles /></span><div><small>BOOM TENNIS</small><strong>网爆星球 V4</strong></div></div>
        <div className="login-copy"><span>综合运营管理平台</span><h1>让每一次服务、教学与成长，都有清晰的业务事实。</h1><p>四个角色端口，一个权限底座。移动任务优先，总部治理清晰。</p></div>
        <div className="login-principles"><span><ShieldIcon />角色与门店隔离</span><span><Building2 />武汉四店统一口径</span></div>
      </section>
      <Card className="login-panel">
        <div className="login-panel__heading"><Smartphone /><div><span>{auth.mode === 'demo' ? '工程底座验收' : '内部账号登录'}</span><h2>{auth.mode === 'demo' ? '选择角色查看四端' : '手机号 + 密码登录'}</h2></div></div>
        {auth.error && <div className="form-error" role="alert">{auth.error}</div>}
        {auth.mode === 'demo' ? (
          <div className="role-login-grid">
            {demoRoles.map((item) => <button key={item.role} onClick={() => auth.signInAsDemo(item.role)}><div><strong>{item.label}</strong><span>{item.scope}</span></div><ArrowRight /></button>)}
          </div>
        ) : (
          <form onSubmit={loginForm.handleSubmit(({ phone, password }) => auth.signInWithPassword(phone, password))}>
            <label>手机号<input inputMode="numeric" autoComplete="username" placeholder="请输入已建档手机号" {...loginForm.register('phone')} /></label>
            {loginForm.formState.errors.phone && <p className="field-error">{loginForm.formState.errors.phone.message}</p>}
            <label>密码<input type="password" inputMode="numeric" autoComplete="current-password" placeholder="请输入8位登录密码" {...loginForm.register('password')} /></label>
            {loginForm.formState.errors.password && <p className="field-error">{loginForm.formState.errors.password.message}</p>}
            <Button type="submit" disabled={loginForm.formState.isSubmitting}>登录</Button>
          </form>
        )}
        <p className="login-note">{auth.mode === 'demo' ? '演示会话只用于本地工程验收，不连接生产数据，也不绕过正式认证。' : '仅限后台预先创建并处于启用状态的内部账号登录。'}</p>
      </Card>
    </main>
  );
}

function ShieldIcon() { return <span className="mini-shield">✓</span>; }
